import { HistoryEntry } from "@drillflo/drillcore/lib/Collaboration";
import { CollaborationReturn } from "../../hooks/useCollaboration";
import { useCallback, useState } from "react";

export interface CollaborationHistoryModalProps {
  collaborationReturn: CollaborationReturn;
}

export default function CollaborationHistory({
  collaborationReturn,
}: CollaborationHistoryModalProps) {
  const {
    collaboration,
    getVersionHistory,
    collaborationHistoryLoading,
    currentDrillVersion,
    forceStateUpdate: forceCollaborationStateUpdate,
  } = collaborationReturn;

  // Collaboration history menu state
  const [showCollaborationHistory, setShowCollaborationHistory] =
    useState<boolean>(false);
  const [collaborationHistory, setCollaborationHistory] = useState<
    HistoryEntry[]
  >([]);

  // Revert confirmation modal state
  const [showRevertConfirmModal, setShowRevertConfirmModal] =
    useState<boolean>(false);
  const [versionToRevert, setVersionToRevert] = useState<string | null>(null);

  const handleShowCollaborationHistory = useCallback(() => {
    if (collaboration) {
      getVersionHistory((history: HistoryEntry[]) => {
        setCollaborationHistory(history);
        setShowCollaborationHistory(true);
      });
    }
  }, [collaboration, getVersionHistory]);

  const handleRevertClick = useCallback((versionId: string) => {
    setVersionToRevert(versionId);
    setShowRevertConfirmModal(true);
  }, []);

  const confirmRevert = useCallback(() => {
    if (collaboration && versionToRevert) {
      collaboration.revert(versionToRevert);
    }
    setShowCollaborationHistory(false);
    setShowRevertConfirmModal(false);
    setVersionToRevert(null);
    forceCollaborationStateUpdate();
  }, [collaboration, versionToRevert, forceCollaborationStateUpdate]);

  // Function to cancel revert
  const cancelRevert = useCallback(() => {
    setShowRevertConfirmModal(false);
    setVersionToRevert(null);
  }, []);

  return (
    <div>
      {collaboration && (
        <button
          onClick={handleShowCollaborationHistory}
          disabled={!collaboration.isConnected || collaborationHistoryLoading}
          className={`p-1.5 rounded-md transition-colors ${
            collaboration.isConnected && !collaborationHistoryLoading
              ? "text-white hover:bg-white/10"
              : "text-white/40"
          }`}
          title="Version History"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12,6 12,12 16,14" />
          </svg>
        </button>
      )}

      {showCollaborationHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Version History</h2>
              <button
                onClick={() => setShowCollaborationHistory(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {collaborationHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No version history available
                </p>
              ) : (
                <div className="space-y-3">
                  {collaborationHistory.map((entry, index) => {
                    const isCurrentVersion = entry.id === currentDrillVersion;
                    return (
                      <div
                        key={entry.id}
                        className={`relative p-4 border rounded-lg transition-all ${
                          isCurrentVersion
                            ? "border-green-300 bg-gradient-to-r from-green-50 to-green-100"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {isCurrentVersion && (
                          <div className="absolute -top-2 left-4 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                            Current
                          </div>
                        )}

                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 mb-1">
                              {entry.description}
                            </div>
                            <div className="text-xs text-gray-500 mb-1">
                              {new Date(entry.timestamp).toLocaleString()}
                            </div>
                            {entry.userEmail && (
                              <div className="text-xs text-gray-400">
                                by {entry.userEmail}
                              </div>
                            )}
                          </div>

                          <div className="flex-shrink-0 ml-4">
                            {!isCurrentVersion ? (
                              <button
                                onClick={() => handleRevertClick(entry.id)}
                                className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 hover:border-blue-300 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                              >
                                Revert
                              </button>
                            ) : (
                              <div className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-100 border border-green-200 rounded-md">
                                Active
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showRevertConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full mx-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-orange-600"
              >
                <path d="M3 7v6h6"></path>
                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-center mb-2">
              Revert to Previous Version
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to revert to this previous version? You will
              still be able to revert to the current version later if needed.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelRevert}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmRevert}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Revert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
